import Button from '@mui/material/Button';
import React from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import type { Job, JobStepStatus, UserIdentifier, UserSummary } from '../../../app/ApiGen';
import { useListJobs } from '../../../app/ApiGen';
import { DateRangePicker } from '../../../components/DateRange';
import { DataErrorHandler } from '../../../components/ErrorHandler';
import { ExportCsv, type ExportProps } from '../../../components/ExportCsv';
import { TableLink } from '../../../components/Link';
import type { ColumnChangerProps, SearchFieldProps } from '../../../design-system';
import { Card, ColumnChanger, DataTable, SearchField, Workspace } from '../../../design-system';
import type { ColumnDefinitions } from '../../../utils/dataTable/columns';
import { navigateOnRowClick } from '../../../utils/dataTable/rowHelpers';
import { getCondensedDate } from '../../../utils/dateFormat';
import { type DateRangeProps, useEndDateState, useSetDateRange, useStartDateState } from '../../../utils/dateRange';
import { renderUserList } from '../../../utils/format';
import { CommissioningStepFilters } from '../../../utils/jobs/filterComponents/CommissioningStepFilters';
import { InstallationStageFilters } from '../../../utils/jobs/filterComponents/InstallationStageFilters';
import { filterBySelectableItem } from '../../../utils/jobs/filterComponents/SelectableFilters';
import { TextFieldFilter } from '../../../utils/jobs/filterComponents/TextFieldFilter';
import { YesNoFilters, type YesNoOption } from '../../../utils/jobs/filterComponents/YesNoFilters';
import { YesNoNAFilters, YesNoNAOption } from '../../../utils/jobs/filterComponents/YesNoNAFilters';
import {
  type CommissioningStepName,
  getCurrentCommissioningStep,
  getCurrentInstallationStage,
  HumanReadableCommissioningStep,
  HumanReadableInstallationStage,
  type InstallationStageName,
} from '../../../utils/jobs/stages';
import { jobColumns, type JobListData, jobListOptions, toJobListData } from '../../../utils/jobs/table';
import { normalise, type Normalised } from '../../../utils/request';
import { userListIncludesSearch } from '../../../utils/search';

type AdminJobData = JobListData & {
  assignedInstallers: (UserIdentifier | Normalised<UserSummary>)[];
  assignedInstallersLabel: string;
  commissioningStep?: CommissioningStepName;
  commissioningStepLabel: string;
  data: string;
  installationStage?: InstallationStageName;
  installationStageLabel: string;
  isThirdParty: YesNoOption;
  isHardwareOnly: YesNoOption;
  cocCompleted: YesNoNAOption;
};

const DefaultDaysAgo = 60;

function toAdminJobData(jobs: Normalised<Job>[]): AdminJobData[] {
  return jobs.map((job) => {
    const progress = job.details.progress as
      | {
          certificateOfCompliance: JobStepStatus | undefined;
        }
      | undefined;
    let cocStatus: YesNoNAOption = 'N/A';
    if (progress) {
      cocStatus = progress.certificateOfCompliance === 'completed' ? 'Yes' : 'No';
    }
    const installationStage = getCurrentInstallationStage(job.details.installationStages);
    const commissioningStep = getCurrentCommissioningStep(job.details.installationStages);
    return {
      ...toJobListData(job),
      assignedInstallers: job.assignedInstallers,
      assignedInstallersLabel: renderUserList(job.assignedInstallers, 'installer'),
      installationStage,
      installationStageLabel: installationStage ? HumanReadableInstallationStage[installationStage] : '',
      commissioningStep,
      commissioningStepLabel: commissioningStep ? HumanReadableCommissioningStep[commissioningStep] : '',
      isThirdParty: job.isThirdParty ? 'Yes' : 'No',
      isHardwareOnly: job.isHardwareOnly ? 'Yes' : 'No',
      cocCompleted: cocStatus,
      data: JSON.stringify(job, null, 2),
    };
  });
}

type JobListSearchFieldProps = SearchFieldProps & DateRangeProps;

const JobsList: React.FC = () => {
  const history = useHistory();
  const { url } = useRouteMatch();

  const [fromDate, setFromDate] = useStartDateState(DefaultDaysAgo);
  const [toDate, setToDate] = useEndDateState();
  const setDateRange = useSetDateRange(setFromDate, setToDate);

  const { data, error, loading, refetch } = useListJobs({
    queryParams: { from: fromDate.toISOString(), to: toDate.toISOString() },
  });

  if (error) {
    return <DataErrorHandler description="Unable to load jobs" error={error} refetch={refetch} />;
  }

  const tableData: AdminJobData[] = data ? toAdminJobData(normalise(data)) : [];
  const columns = jobColumns as ColumnDefinitions<AdminJobData>;

  return (
    <Workspace maxWidth={false}>
      <Card>
        <DataTable<AdminJobData>
          isLoading={loading}
          options={jobListOptions<AdminJobData>()}
          toolbarProps={{
            search: (props: JobListSearchFieldProps) => (
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ paddingBottom: 8 }}>
                  <DateRangePicker fromDate={fromDate} toDate={toDate} onApply={setDateRange} />
                </span>
                <SearchField
                  dataManager={props.dataManager}
                  item="jobs"
                  onSearchChanged={props.onSearchChanged}
                  search={props.search}
                />
              </span>
            ),
            actions: (props: ColumnChangerProps & ExportProps<AdminJobData>) => (
              <>
                <ColumnChanger
                  columnsButton={props.columnsButton}
                  columns={props.columns}
                  icons={props.icons}
                  onColumnsChanged={props.onColumnsChanged}
                />
                <ExportCsv<AdminJobData>
                  columns={props.columns}
                  data={props.data}
                  exportFileName={props.exportFileName}
                  getFieldValue={props.getFieldValue}
                  icons={props.icons}
                />
                <Button color="primary" component={Link} to="/admin/create/job" variant="contained">
                  Create job
                </Button>
              </>
            ),
          }}
          columns={[
            {
              field: 'updated',
              title: 'Last Updated',
              type: 'date',
              defaultSort: 'desc',
              render: ({ id, updated }) => <TableLink to={`${url}/${id}`}>{getCondensedDate(updated)}</TableLink>,
              filterComponent: TextFieldFilter,
            },
            {
              field: 'created',
              title: 'Created',
              type: 'date',
              hidden: true,
              render: ({ created }) => getCondensedDate(created),
              filterComponent: TextFieldFilter,
              width: '10%',
            },
            columns.referenceId,
            columns.type,
            columns.status,
            {
              field: 'installationStageLabel',
              title: 'Installation Stage',
              hidden: true,
              filterComponent: InstallationStageFilters,
              customFilterAndSearch: (search: unknown, { installationStage }) =>
                filterBySelectableItem<InstallationStageName | 'none'>(search, {
                  value: installationStage ?? 'none',
                }),
            },
            {
              field: 'commissioningStepLabel',
              title: 'Commissioning Step',
              hidden: true,
              filterComponent: CommissioningStepFilters,
              customFilterAndSearch: (search: unknown, { commissioningStep }) =>
                filterBySelectableItem<CommissioningStepName | 'none'>(search, {
                  value: commissioningStep ?? 'none',
                }),
            },
            {
              field: 'cocCompleted',
              title: 'CoC Completed',
              filterComponent: YesNoNAFilters,
              customFilterAndSearch: (search: unknown, { cocCompleted }) =>
                filterBySelectableItem<YesNoNAOption>(search, { value: cocCompleted }),
            },
            columns.organisationName,
            columns.customerName,
            columns.email,
            columns.address,
            columns.icpNumber,
            {
              field: 'assignedInstallersLabel',
              title: 'Assigned Installer(s)',
              filterComponent: TextFieldFilter,
              customFilterAndSearch: (search: unknown, { assignedInstallers, assignedInstallersLabel }) =>
                userListIncludesSearch({ users: assignedInstallers, label: assignedInstallersLabel, search }),
            },
            columns.installerLabel,
            {
              field: 'isThirdParty',
              title: 'Third-party',
              filterComponent: YesNoFilters,
              customFilterAndSearch: (search: unknown, { isThirdParty }) =>
                filterBySelectableItem<YesNoOption>(search, { value: isThirdParty }),
            },
            {
              field: 'isHardwareOnly',
              title: 'Hardware-only',
              filterComponent: YesNoFilters,
              customFilterAndSearch: (search: unknown, { isHardwareOnly }) =>
                filterBySelectableItem<YesNoOption>(search, { value: isHardwareOnly }),
            },
            {
              field: 'data',
              title: 'Raw Job Data (JSON)',
              hidden: true,
              render: ({ data: rawJSON }) => <pre>{rawJSON}</pre>,
              filterComponent: TextFieldFilter,
            },
          ]}
          data={tableData}
          onRowClick={navigateOnRowClick((rowData) => `${url}/${rowData.id}`, history)}
        />
      </Card>
    </Workspace>
  );
};

export default JobsList;
